// libraries.
import { useState } from "react";
import { toast } from "react-toastify";

// hooks.
import { useUserProfile } from "@shared/GoogleLoginButton/user-slice/useUserProfile.hook";
import { useLazyGetDomainApprovalQuery } from "@store/apiSlices/domainApproval/domainApproval.hooks";
import { useTriggerNotarizationSupportMutation } from "@store/apiSlices/notarizationSupport/notarizationSupport.hooks";
import { useCorrectDocumentDomainMutation } from "@store/apiSlices/documentDomainCorrection/documentDomainCorrection.hooks";

interface ProcessProgress {
  current: number;
  total: number;
}

export const useProcessNids = () => {
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [progress, setProgress] = useState<ProcessProgress>({
    current: 0,
    total: 0,
  });

  // Hooks
  const { country } = useUserProfile();
  const [getDomainApproval] = useLazyGetDomainApprovalQuery();
  const [triggerNotarization] = useTriggerNotarizationSupportMutation();
  const [correctDocument] = useCorrectDocumentDomainMutation();

  const processNids = async (nidsString: string): Promise<boolean> => {
    const nidsArray = nidsString
      .split(",")
      .map((nid) => nid.trim())
      .filter((nid) => nid !== "");

    // Validaciones
    if (nidsArray.length === 0) {
      toast.error("Por favor ingresa al menos un NID válido");
      return false;
    }

    if (!country) {
      toast.error("No se pudo obtener el país del usuario");
      return false;
    }

    setIsProcessing(true);
    setProgress({ current: 0, total: nidsArray.length });

    const successfulNids: string[] = [];
    const failedNids: string[] = [];

    // Procesar cada NID secuencialmente
    for (let i = 0; i < nidsArray.length; i++) {
      const nid = nidsArray[i];
      setProgress({ current: i + 1, total: nidsArray.length });

      try {
        // 1. Domain Approval
        toast.loading(`Procesando NID ${nid} - Aprobología dominio...`, {
          toastId: `${nid}-domain`,
        });
        await getDomainApproval({ country, nid }).unwrap();
        toast.dismiss(`${nid}-domain`);

        // 2. Notarization Support
        toast.loading(`Procesando NID ${nid} - Notarización...`, {
          toastId: `${nid}-notarization`,
        });
        await triggerNotarization({ country, nid }).unwrap();
        toast.dismiss(`${nid}-notarization`);

        // 3. Document Domain Correction
        toast.loading(`Procesando NID ${nid} - Corrección de dominio...`, {
          toastId: `${nid}-correction`,
        });
        await correctDocument({ country, nid }).unwrap();
        toast.dismiss(`${nid}-correction`);

        // Éxito
        successfulNids.push(nid);
        toast.success(`NID ${nid} procesado exitosamente`, {
          autoClose: 3000,
        });
      } catch (error) {
        failedNids.push(nid);
        toast.dismiss(`${nid}-domain`);
        toast.dismiss(`${nid}-notarization`);
        toast.dismiss(`${nid}-correction`);
        toast.error(`Error procesando NID ${nid}`, {
          autoClose: 5000,
        });
        console.error(`Error procesando NID ${nid}:`, error);
      }
    }

    // Resumen final
    setIsProcessing(false);
    setProgress({ current: 0, total: 0 });

    if (successfulNids.length > 0) {
      toast.success(`${successfulNids.length} NID(s) procesados exitosamente`, {
        autoClose: 5000,
      });
    }

    if (failedNids.length > 0) {
      toast.warning(
        `${failedNids.length} NID(s) fallaron: ${failedNids.join(", ")}`,
        {
          autoClose: 7000,
        },
      );
    }

    // Retornar true si todos fueron exitosos
    return failedNids.length === 0;
  };

  return {
    isProcessing,
    processNids,
    progress,
  };
};
